import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Edit, Building2, Download, FileSpreadsheet, ExternalLink, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, flattenObject } from '@/utils/exportCSV';
import { exportToExcel, flattenObjectForExcel, formatDataForExcel } from '@/utils/exportExcel';
import BulkActionBar from '../../components/BulkActionBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 20;

  useEffect(() => {
    fetchCompanies();
  }, [skip, typeFilter]);

  const fetchCompanies = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);
      if (typeFilter) params.append('company_type', typeFilter);

      const response = await axios.get(`${API}/admin/companies?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompanies(response.data.companies);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load companies');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchCompanies();
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(companies.map(company => company.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (companyId) => {
    if (selectedIds.includes(companyId)) {
      setSelectedIds(selectedIds.filter(id => id !== companyId));
    } else {
      setSelectedIds([...selectedIds, companyId]);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkAction = async (action) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/admin/companies/bulk-action`, {
        ids: selectedIds,
        action: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      if (result.success_count > 0) {
        toast.success(`Successfully ${action}d ${result.success_count} company(ies)`);
        setSelectedIds([]);
        fetchCompanies();
      }
      if (result.failed_count > 0) {
        toast.error(`Failed to ${action} ${result.failed_count} company(ies)`);
      }
      
      return result;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} companies`);
      throw error;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      partner: 'bg-blue-100 text-blue-800',
      freelance: 'bg-purple-100 text-purple-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type?.toUpperCase() || 'N/A'}
      </span>
    );
  };

  const getSubscriptionBadge = (status) => {
    const colors = {
      trial: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.toUpperCase() || 'N/A'}
      </span>
    );
  };

  const getCompletionBadge = (completion, completed) => {
    if (completed) {
      return (
        <div className="flex items-center gap-1">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-green-600 text-sm">{completion}%</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1">
        <AlertCircle className="w-4 h-4 text-orange-500" />
        <span className="text-orange-600 text-sm">{completion}%</span>
      </div>
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Companies Management</h1>
        <p className="text-gray-600 mt-2">View and manage all companies on the platform</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 flex items-center space-x-2">
              <Input
                placeholder="Search by company name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} style={{ backgroundColor: '#62D84E' }}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
            
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setSkip(0);
              }}
              className="border rounded-md px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="partner">Partner</option>
              <option value="freelance">Freelance</option>
            </select>

            <Button variant="outline" onClick={() => {
              const flattenedCompanies = companies.map(company => flattenObject(company));
              exportToCSV(flattenedCompanies, 'companies_export');
              toast.success('Companies exported to CSV successfully');
            }}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>

            <Button variant="outline" onClick={() => {
              const flattenedCompanies = companies.map(company => flattenObjectForExcel(company));
              const formattedCompanies = formatDataForExcel(flattenedCompanies, {
                id: 'Company ID',
                name: 'Company Name',
                company_type: 'Type',
                subscription_tier: 'Subscription Tier',
                subscription_status: 'Status',
                created_at: 'Registration Date'
              });
              exportToExcel(formattedCompanies, `companies_export_${new Date().toISOString().split('T')[0]}`, 'Companies');
              toast.success('Companies exported to Excel successfully');
            }}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No companies found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === companies.length && companies.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Users</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Resources</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Projects</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Profile</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Intervention</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {companies.map((company) => (
                    <tr key={company.id} className={`hover:bg-gray-50 ${company.needs_intervention ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(company.id)}
                          onChange={() => handleSelectOne(company.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <Building2 className="w-5 h-5 mr-2 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{company.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">{getTypeBadge(company.type || company.company_type)}</td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-sm font-semibold text-gray-700">{company.users_count || 0}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-sm font-semibold text-gray-700">{company.resources_count || 0}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-sm font-semibold text-gray-700">{company.projects_count || 0}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {getCompletionBadge(company.profile_completion || 0, company.profile_completed)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {company.needs_intervention ? (
                          <Badge variant="destructive" className="text-xs">Needs Action</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-green-600">OK</Badge>
                        )}
                      </td>
                      <td className="px-4 py-4">{getSubscriptionBadge(company.subscription_status)}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <a 
                            href={`/company/${company.id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-1 hover:bg-gray-100 rounded"
                            title="View public page"
                          >
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </a>
                          <Link to={`/admin/companies/${company.id}`}>
                            <Button size="sm" variant="ghost">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} companies
        </p>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClearSelection={handleClearSelection}
        actions={[
          { label: 'Delete', value: 'delete', color: 'text-red-600 border-red-600', icon: Trash2 }
        ]}
        onExecute={handleBulkAction}
        entityType="companies"
      />
    </div>
  );
};

export default CompaniesPage;
